import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Loader2, ArrowRight, Mic2 } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function OrderSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);

  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await api.getCheckoutStatus(sessionId);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          setPaymentInfo(data);
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setTimeout(() => pollStatus(attempts + 1), pollInterval);
      } catch (error) {
        // Retry on transient failure
        if (attempts < maxAttempts - 1) {
          setTimeout(() => pollStatus(attempts + 1), pollInterval);
        } else {
          setStatus('error');
        }
      }
    };

    if (isAuthenticated) {
      pollStatus();
    } else {
      // Redirect to login if not authenticated
      navigate('/login', { state: { returnTo: `/order/success?session_id=${sessionId}&order_id=${orderId}` } });
    }
  }, [sessionId, orderId, isAuthenticated, navigate]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <motion.div
        {...fadeInUp}
        className="max-w-md w-full bg-surface border border-[#262626] rounded-2xl p-8 text-center"
      >
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="w-8 h-8 bg-lime rounded-full flex items-center justify-center">
            <Mic2 className="w-4 h-4 text-black" />
          </div>
          <span className="font-heading font-bold text-lg">Tune4Tune</span>
        </Link>

        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-6 animate-spin text-lime" />
            <h1 className="font-heading text-2xl font-bold mb-2">Processing Payment...</h1>
            <p className="text-[#A1A1AA]">Please wait while we confirm your payment</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-lime/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-lime" />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">Payment Successful!</h1>
            <p className="text-[#A1A1AA] mb-6">
              Your dubplate request has been submitted. The artist will be notified and you'll receive updates via email.
            </p>
            {paymentInfo && (
              <div className="bg-surface-highlight rounded-lg p-4 mb-6">
                <p className="text-sm text-[#A1A1AA]">Amount Paid</p>
                <p className="font-mono text-2xl font-bold text-lime">
                  ${(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                </p>
              </div>
            )}
            <div className="flex flex-col gap-3">
              <Link to="/dashboard" data-testid="go-to-dashboard">
                <Button className="w-full btn-primary">
                  View My Orders
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full border-[#262626]">
                  Back to Artists
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === 'timeout' && (
          <>
            <h1 className="font-heading text-2xl font-bold mb-2">Payment Pending</h1>
            <p className="text-[#A1A1AA] mb-6">
              We're still processing your payment. Please check your dashboard for updates.
            </p>
            <Link to="/dashboard">
              <Button className="btn-primary">Go to Dashboard</Button>
            </Link>
          </>
        )}

        {status === 'expired' && (
          <>
            <h1 className="font-heading text-2xl font-bold mb-2">Session Expired</h1>
            <p className="text-[#A1A1AA] mb-6">
              Your payment session has expired. Please try again.
            </p>
            <Link to="/">
              <Button className="btn-primary">Browse Artists</Button>
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <h1 className="font-heading text-2xl font-bold mb-2">Something Went Wrong</h1>
            <p className="text-[#A1A1AA] mb-6">
              We couldn't verify your payment. Please contact support if you were charged.
            </p>
            <Link to="/">
              <Button className="btn-primary">Back to Home</Button>
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
